import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneCallIcon, VideoIcon, MessageSquareIcon, UsersIcon, ScalingIcon, DollarSignIcon, ShieldCheckIcon, ArrowRightIcon, CloudIcon, MonitorSmartphoneIcon, CheckCircle2Icon, GlobeIcon, MicIcon, Settings2Icon, LayoutDashboardIcon, SmartphoneIcon, BarChart3Icon, LayersIcon, UserPlusIcon, LifeBuoyIcon, LaptopIcon, Contact2Icon, BookOpenIcon, ArrowRightLeftIcon, SettingsIcon, HelpCircleIcon, ChevronDownIcon, BlocksIcon, BriefcaseIcon, MailIcon, MessageCircleIcon, PieChartIcon } from 'lucide-react';
import { PricingSection } from '../components/PricingSection';
const features = [{
  icon: ScalingIcon,
  title: 'Scalable to Your Needs',
  description: "Whether you're running a small business or managing a large enterprise, our UCaaS solution grows with you. Easily add or remove users as needed, giving you the flexibility to scale without limits.",
  color: 'text-blue-500',
  bg: 'bg-blue-50'
}, {
  icon: DollarSignIcon,
  title: 'Cost-Efficient Solution',
  description: 'Say goodbye to the high costs of traditional phone systems. Our cloud-based UCaaS solution offers predictable pricing and eliminates the need for expensive hardware, helping you save money while enhancing communication.',
  color: 'text-green-500',
  bg: 'bg-green-50'
}, {
  icon: ShieldCheckIcon,
  title: 'Unmatched Reliability',
  description: 'With 99.99% uptime, our UCaaS platform ensures your business stays connected without interruptions. Powered by cutting-edge technology, you can trust that your communications will always be reliable.',
  color: 'text-purple-500',
  bg: 'bg-purple-50'
}];
const ucaasFeatures = [{
  title: 'Unlimited Calling',
  description: 'Unlimited local and long-distance calling across the US and Canada.',
  icon: PhoneCallIcon
}, {
  title: 'Business Messaging',
  description: 'Send and receive professional text messages from your business number.',
  icon: MessageSquareIcon
}, {
  title: 'Video Meetings',
  description: 'HD video conferencing with screen sharing and team collaboration.',
  icon: VideoIcon
}, {
  title: 'Visual Voicemail',
  description: 'Get your voicemails delivered to your email or mobile app as text.',
  icon: MicIcon
}, {
  title: 'Advanced Call Routing',
  description: 'Custom call flows, multi-level IVR, and auto-attendant features.',
  icon: Settings2Icon
}, {
  title: 'Team Collaboration',
  description: 'File sharing, task management, and team chat in one workspace.',
  icon: UsersIcon
}, {
  title: 'Mobile & Desktop Apps',
  description: 'Stay connected from anywhere with dedicated apps for all devices.',
  icon: SmartphoneIcon
}, {
  title: 'Analytics & Reporting',
  description: 'Real-time insights into call volume, usage, and team performance.',
  icon: LayoutDashboardIcon
}, {
  title: 'Global Reach',
  description: 'International numbers and global connectivity for your business.',
  icon: GlobeIcon
}];
const tools = [{
  icon: PhoneCallIcon,
  label: 'Voice Calls',
  color: 'text-blue-500',
  bg: 'bg-blue-50'
}, {
  icon: VideoIcon,
  label: 'Video Meetings',
  color: 'text-red-500',
  bg: 'bg-red-50'
}, {
  icon: MessageSquareIcon,
  label: 'Instant Messaging',
  color: 'text-green-500',
  bg: 'bg-green-50'
}, {
  icon: UsersIcon,
  label: 'Collaboration',
  color: 'text-amber-500',
  bg: 'bg-amber-50'
}, {
  icon: CloudIcon,
  label: 'Cloud-Based',
  color: 'text-violet-500',
  bg: 'bg-violet-50'
}, {
  icon: MonitorSmartphoneIcon,
  label: 'Any Device',
  color: 'text-teal-500',
  bg: 'bg-teal-50'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4
    }
  }
};
export function UCaaSPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  return <div data-id="element-1943">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-1944">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-1945"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-1946"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/3 -translate-y-1/4 w-[700px] h-[700px] bg-indigo-500/15 rounded-full blur-[120px] pointer-events-none" data-id="element-1947"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1948">
          <div className="max-w-3xl" data-id="element-1949">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-1950">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1951">
                <span className="flex h-2 w-2 rounded-full bg-indigo-400 mr-2" data-id="element-1952"></span>
                Cloud-Based Communications
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-1953">
                Unified Cloud
                <br className="hidden md:block" data-id="element-1954" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-1955">
                  Communications
                </span>
              </h1>
              <p className="text-2xl md:text-3xl text-slate-300 font-medium mb-6" data-id="element-1956">
                Stay connected, Anywhere, Anytime
              </p>
              <p className="text-lg text-slate-400 leading-relaxed mb-8 max-w-2xl" data-id="element-1957">
                All your essential communication tools in one easy-to-use
                platform — voice calls, video meetings, instant messaging, and
                collaboration tools accessible on any device.
              </p>
              <button onClick={() => onNavigate('home')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-1958">
                Modernize Your Office
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1959" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is UCaaS */}
      <section className="py-20 bg-white" data-id="element-1960">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1961">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1962">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1963">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-1964">
                What is UCaaS?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-1965">
                Unified Communications as a Service (UCaaS) is a cloud-based
                solution that brings all your essential communication tools into
                one easy-to-use platform. Imagine having voice calls, video
                meetings, instant messaging, and collaboration tools all in one
                place—accessible anytime, anywhere, on any device. That's the
                power of UCaaS.
              </p>
            </motion.div>

            <motion.div className="grid grid-cols-2 sm:grid-cols-3 gap-4" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-1966">
              {tools.map((tool, i) => {
              const Icon = tool.icon;
              return <motion.div key={i} variants={itemVariants} className="bg-slate-50 rounded-2xl p-5 border border-slate-100 text-center" data-id="element-1967">
                    <div className={`w-12 h-12 ${tool.bg} rounded-xl flex items-center justify-center mx-auto mb-3`} data-id="element-1968">
                      <Icon className={`w-6 h-6 ${tool.color}`} data-id="element-1969" />
                    </div>
                    <span className="text-sm font-semibold text-slate-800" data-id="element-1970">
                      {tool.label}
                    </span>
                  </motion.div>;
            })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-slate-50" data-id="element-1971">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1972">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1973">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1974">
              Why Choose UCaaS?
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1975">
              A smarter, more affordable way to keep your entire team connected
              and productive.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1976">
            {features.map((feature, i) => {
            const Icon = feature.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm flex flex-col" data-id="element-1977">
                  <div className={`w-14 h-14 ${feature.bg} rounded-2xl flex items-center justify-center mb-6`} data-id="element-1978">
                    <Icon className={`w-7 h-7 ${feature.color}`} data-id="element-1979" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1980">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed flex-grow" data-id="element-1981">
                    {feature.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-white" data-id="element-1982">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1983">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1984">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1985">
              Your Journey to Unified Communications
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1986">
              We guide you through every step of modernizing your communication
              stack.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-1987">
            {[{
            title: 'Workflow Analysis',
            desc: 'We study how your team communicates today to identify gaps and opportunities for improvement.',
            icon: BarChart3Icon
          }, {
            title: 'Platform Selection',
            desc: 'We help you choose the UCaaS platform that best fits your existing software ecosystem.',
            icon: LayersIcon
          }, {
            title: 'User Onboarding',
            desc: 'We manage the rollout and ensure every team member is comfortable with the new tools.',
            icon: UserPlusIcon
          }, {
            title: '24/7 Support',
            desc: 'Ongoing management and priority support to ensure your communications never stop.',
            icon: LifeBuoyIcon
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="relative p-8 rounded-2xl border border-slate-100 bg-slate-50" data-id="element-1988">
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6" data-id="element-1989">
                  <step.icon className="w-6 h-6 text-primary" data-id="element-1990" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1991">
                  {step.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-1992">
                  {step.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Migration from Legacy Systems */}
      <section className="py-24 bg-slate-50" data-id="element-1993">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1994">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1995">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1996">
              Seamless Migration from Legacy PBX
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1997">
              Worried about downtime? Don't be. We've migrated hundreds of
              businesses to the cloud with zero disruption.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-1998">
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-slate-200 -translate-y-8 z-0" data-id="element-1999"></div>
            {[{
            icon: BarChart3Icon,
            title: '1. Audit & Plan',
            desc: 'We map your current call flows, extensions, and hardware requirements.'
          }, {
            icon: SettingsIcon,
            title: '2. Configure & Train',
            desc: 'We build the new system in the cloud and train your team before go-live.'
          }, {
            icon: ArrowRightLeftIcon,
            title: '3. Seamless Cutover',
            desc: 'Numbers port over instantly. You unplug the old phones and start using the new system.'
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="relative z-10 text-center" data-id="element-2000">
                <div className="w-16 h-16 bg-white border-4 border-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-2001">
                  <step.icon className="w-8 h-8 text-indigo-600" data-id="element-2002" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-2003">
                  {step.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2004">
                  {step.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Industry Solutions */}
      <section className="py-24 bg-slate-900 relative overflow-hidden" data-id="element-2005">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-2006"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2007">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2008">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2009">
              Industry-Specific UCaaS
            </h2>
            <p className="text-lg text-slate-400" data-id="element-2010">
              Unified communications tailored for the way your specific team
              works.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2011">
            {[{
            title: 'Remote Teams',
            desc: 'Seamless video and messaging that keeps distributed workforces connected as if they were in the same room.',
            icon: LaptopIcon
          }, {
            title: 'Support Centers',
            desc: 'Advanced routing and analytics for high-volume customer service and support environments.',
            icon: Contact2Icon
          }, {
            title: 'Education',
            desc: 'Secure collaboration tools for faculty and students, supporting both in-person and remote learning.',
            icon: BookOpenIcon
          }].map((industry, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white/5 border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-colors" data-id="element-2012">
                <industry.icon className="w-12 h-12 text-primary mb-6" data-id="element-2013" />
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-2014">
                  {industry.title}
                </h3>
                <p className="text-slate-400 leading-relaxed" data-id="element-2015">
                  {industry.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Feature Dump Section */}
      <section className="py-24 bg-white" data-id="element-2016">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2017">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2018">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2019">
              Everything You Need to Succeed
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2020">
              Our UCaaS platform comes packed with enterprise-grade features
              designed to streamline your business communications.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2021">
            {ucaasFeatures.map((feature, i) => {
            const Icon = feature.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="group p-8 rounded-2xl border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300" data-id="element-2022">
                  <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-white transition-colors" data-id="element-2023">
                    <Icon className="w-6 h-6 text-primary group-hover:text-white" data-id="element-2024" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-2025">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-2026">
                    {feature.description}
                  </p>
                </motion.div>;
          })}
          </div>

          <div className="mt-16 p-8 bg-slate-900 rounded-2xl text-center" data-id="element-2027">
            <p className="text-white text-lg mb-6" data-id="element-2028">
              Need a custom feature set or integration? We specialize in
              tailoring UCaaS solutions to your specific business needs.
            </p>
            <button onClick={() => onNavigate('home')} className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-bold transition-colors" data-id="element-2029">
              Request a Custom Setup
            </button>
          </div>
        </div>
      </section>

      {/* Integration Partners */}
      <section className="py-24 bg-slate-50" data-id="element-2030">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2031">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2032">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2033">
              Works With Your Favorite Tools
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2034">
              Our UCaaS platform integrates seamlessly with the software your
              team already uses every day.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2035">
            {[{
            icon: PieChartIcon,
            name: 'Salesforce',
            desc: 'Click-to-dial and automatic call logging directly in your CRM.'
          }, {
            icon: UsersIcon,
            name: 'Microsoft Teams',
            desc: 'Embed enterprise voice capabilities right inside the Teams app.'
          }, {
            icon: MailIcon,
            name: 'Google Workspace',
            desc: 'Schedule video meetings directly from Google Calendar.'
          }, {
            icon: LifeBuoyIcon,
            name: 'Zendesk',
            desc: 'Screen pops with customer info when they call support.'
          }, {
            icon: MessageCircleIcon,
            name: 'Slack',
            desc: 'Start voice and video calls instantly from any Slack channel.'
          }, {
            icon: BriefcaseIcon,
            name: 'HubSpot',
            desc: 'Sync contacts and track call activity on contact records.'
          }].map((partner, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-start gap-4" data-id="element-2036">
                <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-2037">
                  <partner.icon className="w-6 h-6 text-indigo-600" data-id="element-2038" />
                </div>
                <div data-id="element-2039">
                  <h3 className="font-bold text-slate-900 mb-1" data-id="element-2040">
                    {partner.name}
                  </h3>
                  <p className="text-sm text-slate-600" data-id="element-2041">{partner.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Uptime Highlight */}
      <section className="py-16 bg-slate-50" data-id="element-2042">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2043">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-slate-900 rounded-3xl p-10 md:p-16 text-center relative overflow-hidden" data-id="element-2044">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-[80px] pointer-events-none" data-id="element-2045"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/15 rounded-full blur-[60px] pointer-events-none" data-id="element-2046"></div>
            <div className="relative z-10" data-id="element-2047">
              <div className="text-6xl md:text-7xl font-extrabold text-white mb-4" data-id="element-2048">
                99.99%
              </div>
              <div className="text-xl text-slate-300 font-medium mb-2" data-id="element-2049">
                Uptime Guarantee
              </div>
              <p className="text-slate-400 max-w-lg mx-auto" data-id="element-2050">
                Your business stays connected without interruptions, powered by
                cutting-edge cloud technology you can always rely on.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection category="Telephony" data-id="element-2051" />

      {/* UCaaS FAQ */}
      <section className="py-24 bg-white" data-id="element-2052">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2053">
          <div className="text-center mb-16" data-id="element-2054">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 flex items-center justify-center gap-3" data-id="element-2055">
              <HelpCircleIcon className="w-8 h-8 text-indigo-600" data-id="element-2056" />
              UCaaS FAQ
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2057">
              Common questions about moving to cloud communications.
            </p>
          </div>
          <div className="space-y-4" data-id="element-2058">
            {[{
            q: 'Do I need to buy new desk phones?',
            a: 'Not necessarily. Many existing IP phones can be reprogrammed to work with our UCaaS platform. Alternatively, you can skip physical phones entirely and use our desktop and mobile apps.'
          }, {
            q: 'What happens if our internet goes down?',
            a: "That's the beauty of the cloud! If your office loses internet or power, your phone system stays online. Calls can automatically route to mobile phones or an auto-attendant, and voicemails are sent to email."
          }, {
            q: 'Can I keep my current phone numbers?',
            a: 'Yes. We will port your existing local, toll-free, and fax numbers to the new platform seamlessly.'
          }, {
            q: 'Is UCaaS secure?',
            a: 'Extremely. Our platform uses enterprise-grade encryption for voice and video, and complies with major security standards including HIPAA and SOC 2.'
          }, {
            q: 'How is it billed?',
            a: 'UCaaS is billed on a simple, predictable per-user, per-month basis. This includes unlimited domestic calling and all standard features, eliminating surprise long-distance charges.'
          }].map((faq, i) => <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden bg-slate-50" data-id="element-2059">
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-white transition-colors" data-id="element-2060">
                  <span className="font-bold text-slate-900" data-id="element-2061">{faq.q}</span>
                  <motion.div animate={{
                rotate: activeFaq === i ? 180 : 0
              }} data-id="element-2062">
                    <ChevronDownIcon className="w-5 h-5 text-slate-400" data-id="element-2063" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-2064">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden bg-white" data-id="element-2065">
                      <div className="p-6 pt-0 text-slate-600 border-t border-slate-100" data-id="element-2066">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* Experience the Future */}
      <section className="bg-primary relative overflow-hidden py-20" data-id="element-2067">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-2068">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl" data-id="element-2069"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl" data-id="element-2070"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10" data-id="element-2071">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2072">
            Experience the Future of Business Communication
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-2073">
            Transform your business communications with UCaaS and experience
            unparalleled efficiency and connectivity. Our UCaaS solution
            integrates all your communication needs into a single, scalable
            platform, enhancing collaboration, reducing costs, and ensuring
            top-notch security. Don't let outdated systems hold you back—embrace
            the future of communication today.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2074">
            <button onClick={() => onNavigate('home')} className="w-full sm:w-auto bg-white text-primary hover:bg-slate-50 px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center justify-center" data-id="element-2075">
              Contact Us Today
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2076" />
            </button>
            <a href="tel:+4435451747" className="w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-lg transition-colors" data-id="element-2077">
              Call Us: (443) 545-1747
            </a>
          </div>
        </div>
      </section>
    </div>;
}